// chatContext/chatAnchorLabels.ts
//
// What the composer's context pill calls the item on screen (ORA-662). The
// publishers build these, because they are the only place the question number,
// the card front, the title or the file name is known — the anchor that
// reaches the composer is a bare reference.
//
// Client-only. The label never goes on the wire; the server computes its own
// `contextLabel` from the ids when it hydrates the anchor.

/** A flashcard front is prose, so it is cut down to a glance before it fits. */
export const CHAT_ANCHOR_LABEL_DETAIL_MAX_CHARS = 30;

/**
 * A title or a file name is one line, but it can be a long one — and it ends
 * up inside the remove button's aria-label, which a screen reader then has to
 * read out in full.
 */
export const CHAT_ANCHOR_LABEL_TITLE_MAX_CHARS = 48;

/**
 * Entities decoded through the browser's own parser, then markup and runs of
 * whitespace flattened away. Fronts and titles come from content the editors
 * wrote, so they carry tags, `&amp;`, `&#39;`, `&nbsp;` and newlines that
 * would otherwise be printed literally in a pill.
 *
 * The parse happens on a detached element, so nothing it contains is ever
 * attached to the page; scripts and event handlers in `innerHTML` do not run
 * there. Outside a DOM (a node-environment test) the raw text is returned
 * with its markup stripped, which is the same answer for everything that has
 * no entities in it.
 */
const decodeEntities = (value: string): string => {
  if (typeof document === 'undefined') return value;

  const parser = document.createElement('textarea');
  parser.innerHTML = value;

  return parser.value;
};

const toPlainText = (value: string): string =>
  decodeEntities(value.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

/**
 * `"<prefix> · <detail>"`, or nothing when the publisher has no detail worth
 * showing — the pill then falls back to the kind's own noun.
 */
export const toChatAnchorLabel = (
  prefix: string,
  detail: string | null | undefined,
  maxChars?: number
): string | undefined => {
  const text = detail ? toPlainText(detail) : '';

  if (!text) return undefined;

  if (maxChars === undefined || text.length <= maxChars) {
    return `${prefix} · ${text}`;
  }

  return `${prefix} · ${text.slice(0, maxChars).trimEnd()}…`;
};
